import CheckCircle from '@hc-frontend/core-icons/kit/check-circle';
import People from '@hc-frontend/core-icons/kit/people';
import VerifiedUser from '@hc-frontend/core-icons/kit/verified-user';
import { CircularLoader, StepProgress } from '@hc-frontend/core-ui';
import { useEffect, useState } from 'react';

import { Badge } from './badge';

type LoadingViewProps = {
  minLoadingTime: number;
};

export function LoadingView({ minLoadingTime }: LoadingViewProps) {
  const messages = [
    {
      title: 'Calculating Your Savings...',
      subtitle:
        "We're analyzing your eligibility for premium tax credits and cost-sharing reductions",
    },
    {
      title: 'Checking Available Plans...',
      subtitle:
        'Reviewing health insurance options from top carriers in your area',
    },
    {
      title: 'Finding Your Top Options...',
      subtitle:
        'Comparing plans based on your profile to determine potential savings',
    },
    {
      title: 'Preparing Your Results...',
      subtitle:
        'Our licensed insurance agents are ready to help you enroll in an ACA plan today',
    },
  ];

  const [messageIndex, setMessageIndex] = useState(0);
  const message = messages[messageIndex];

  useEffect(() => {
    const delay = minLoadingTime / messages.length;
    if (messageIndex < messages.length - 1) {
      const timer = setTimeout(() => {
        setMessageIndex((prev) => prev + 1);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [messageIndex, messages.length, minLoadingTime]);

  return (
    <div className="bg-gradient-to-br from-[#f7fcfe] to-[#e0f2fe] rounded-2xl shadow-lg px-24-24 py-40-40 flex flex-col items-center justify-center">
      <Badge />
      <StepProgress
        steps={messages.length}
        currentStep={messageIndex}
        className="my-32-32"
      />
      <p className="hc-typography--heading-three text-center animate-in fade-in slide-in-from-bottom-8-8 duration-1000 fill-mode-both">
        {message.title}
      </p>
      <p className="hc-typography--paragraph-md text-center mt-4-4 animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-150 fill-mode-both">
        {message.subtitle}
      </p>
      <div className="flex justify-center mt-32-32 animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-300 fill-mode-both">
        <CircularLoader className="border-4 size-32-32 !border-gray-50 !border-t-secondary-900" />
      </div>
      <div className="flex flex-wrap justify-evenly border-t border-t-gray-50 mt-40-40 pt-24-24 gap-x-16-16 gap-y-8-8 w-full animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-500 fill-mode-both">
        <p className="flex justify-center items-center gap-4-4">
          <CheckCircle className="size-16-16 text-success-800" />
          Secure & Confidential
        </p>
        <p className="flex justify-center items-center gap-4-4">
          <VerifiedUser className="size-16-16 text-success-800" />
          Licensed Insurance Agents
        </p>
        <p className="flex justify-center items-center gap-4-4">
          <People className="size-16-16 text-success-800" />
          Trusted by Millions*
        </p>
      </div>

      <div className="text-center mt-24-24 w-full animate-in fade-in slide-in-from-bottom-8-8 duration-1000 delay-500 fill-mode-both">
        <p className="hc-typography--disclaimer italic">
          *Based on internal Healthcare.com visitor data as of August 2025.
        </p>
      </div>
    </div>
  );
}
